/**
 * LocationsPage: Standort-Verwaltung.
 *
 * Pattern: Tabelle + Add-Button + Edit-Drawer + Delete-Button.
 * Permissions: alle authenticated Users können lesen, planer/admin schreiben.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiCallError, type Location } from "../lib/api";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Label } from "../components/Label";
import { Drawer } from "../components/Drawer";
import { Badge } from "../components/Badge";
import { ListToolbar } from "../components/ListToolbar";
import { ActiveToggle } from "../components/ActiveToggle";
import { useAuth } from "../lib/auth";
import { t } from "../locales/de";
import { matchesListSearch } from "../lib/listFiltering";

type FormState = {
  name: string;
  address: string;
  timezone: string;
  active: boolean;
};

const EMPTY_FORM: FormState = {
  name: "",
  address: "",
  timezone: "Europe/Berlin",
  active: true,
};

export function LocationsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Location | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const canWrite = user?.role === "admin" || user?.role === "planer";
  const canDelete = user?.role === "admin";

  const { data, isLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: () => api.listLocations(),
  });

  const createMut = useMutation({
    mutationFn: (body: Partial<Location>) => api.createLocation(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["locations"] });
      setDrawerOpen(false);
    },
    onError: (e) => setError(e instanceof ApiCallError ? e.message : "Fehler"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Location> }) =>
      api.updateLocation(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["locations"] });
      setDrawerOpen(false);
    },
    onError: (e) => setError(e instanceof ApiCallError ? e.message : "Fehler"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteLocation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["locations"] }),
    onError: (e) =>
      alert(e instanceof ApiCallError ? e.message : "Löschen fehlgeschlagen"),
  });

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setDrawerOpen(true);
  }

  function openEdit(loc: Location) {
    setEditing(loc);
    setForm({
      name: loc.name,
      address: loc.address ?? "",
      timezone: loc.timezone,
      active: loc.isActive,
    });
    setError(null);
    setDrawerOpen(true);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      name: form.name.trim(),
      address: editing ? form.address.trim() : form.address.trim() || undefined,
      timezone: form.timezone.trim() || "Europe/Berlin",
      active: form.active,
    };
    if (editing) {
      updateMut.mutate({ id: editing.id, body });
    } else {
      createMut.mutate(body);
    }
  }

  const locations = data?.locations ?? [];
  const visibleLocations = locations.filter((location) => matchesListSearch(search, [location.name, location.address, location.timezone]));
  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.nav.locations}</h1>
        {canWrite && (
          <Button onClick={openCreate}>+ Neuer Standort</Button>
        )}
      </div>

      <ListToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Name, Adresse oder Zeitzone suchen…" resultCount={visibleLocations.length} hasActiveFilters={Boolean(search)} onReset={() => setSearch("")} />

      <div className="rounded-lg border border-slate-200 bg-white">
        {isLoading ? (
          <div className="p-6 text-sm text-slate-500">Lade…</div>
        ) : visibleLocations.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Keine Standorte für die aktuelle Suche.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Adresse</th>
                <th className="px-4 py-3">Zeitzone</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {visibleLocations.map((loc) => (
                <tr key={loc.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900">{loc.name}</td>
                  <td className="px-4 py-3 text-slate-600">{loc.address ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{loc.timezone}</td>
                  <td className="px-4 py-3">
                    {loc.isActive ? (
                      <Badge variant="success">Aktiv</Badge>
                    ) : (
                      <Badge variant="neutral">Inaktiv</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canWrite && (
                        <ActiveToggle
                          checked={loc.isActive}
                          disabled={updateMut.isPending}
                          label={`Standort ${loc.name}`}
                          onChange={() => updateMut.mutate({ id: loc.id, body: { active: !loc.isActive } as Partial<Location> })}
                        />
                      )}
                      {canWrite && (
                        <Button variant="secondary" onClick={() => openEdit(loc)}>
                          Bearbeiten
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Standort "${loc.name}" wirklich löschen? Standorte mit zugeordneten Räumen, Ressourcen oder Sessions können nur deaktiviert werden.`)) {
                              deleteMut.mutate(loc.id);
                            }
                          }}
                        >
                          Löschen
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editing ? "Standort bearbeiten" : "Neuer Standort"}
      >
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="address">Adresse</Label>
            <Input
              id="address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="timezone">Zeitzone</Label>
            <Input
              id="timezone"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="active"
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300"
            />
            <Label htmlFor="active" className="!mb-0">Aktiv</Label>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <Button variant="secondary" type="button" onClick={() => setDrawerOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Speichere…" : editing ? "Speichern" : "Anlegen"}
            </Button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}
